// src/native/auth/nativeAuthSurface.js
//
// WS-2 AUTH-NATIVE. Decides what the sign-in screen is allowed to show, and
// handles the one flow that genuinely has to leave the app.
//
// Every function here returns the web answer unchanged when isNative() is
// false. That is not a convention, it is the contract: Login.jsx calls these
// instead of its current inline booleans, so if any of them drifted, the web
// login screen would visibly change. The tests to run are in the patch file.

import posthog from 'posthog-js';
import { isNative, loadNative } from '@/lib/platform';
import { WEB_PASSWORD_RESET_URL } from './nativeAuthConfig';

/**
 * Guideline 4.8. If an app offers any third party or social login service, it
 * must also offer Sign in with Apple. Shipping Google or Facebook without Apple
 * is an automatic rejection, and a build time env flag is a deploy time hope
 * rather than enforcement: nothing stops a future .env from setting
 * VITE_ENABLE_FACEBOOK_LOGIN=1 and leaving VITE_ENABLE_APPLE_LOGIN unset.
 *
 * So the rule lives in code. On native, no third party button renders unless
 * Apple is available. The worst case is a native build that offers email and
 * password only, which ships; the alternative worst case is a rejection.
 *
 * On web the answer is identical to what Login.jsx renders today: Google always,
 * Facebook and Apple behind their env flags.
 *
 * @param {{ facebookEnabled?: boolean, appleEnabled?: boolean }} flags
 * @returns {{ showGoogle: boolean, showFacebook: boolean, showApple: boolean, socialAllowed: boolean }}
 */
export function nativeSocialLoginPolicy({ facebookEnabled = false, appleEnabled = false } = {}) {
  if (!isNative()) {
    return {
      showGoogle: true,
      showFacebook: !!facebookEnabled,
      showApple: !!appleEnabled,
      socialAllowed: true,
    };
  }

  const socialAllowed = !!appleEnabled;
  return {
    showGoogle: socialAllowed,
    showFacebook: !!facebookEnabled && socialAllowed,
    showApple: !!appleEnabled,
    socialAllowed,
  };
}

/**
 * True when the magic link affordances must be hidden.
 *
 * A magic link is an email that opens in Mail and then Safari. The PKCE verifier
 * for that request lives in the native webview's localStorage, which Safari
 * cannot read, so the link lands the user on the website signed out and confused
 * while the app still waits. Universal Links would not rescue it either: the
 * Supabase link goes to /auth/v1/verify, which 302s to the redirect target, and a
 * server side redirect that begins in Safari does not reliably trigger an
 * associated domain. Hiding the affordance is honest; a button that cannot work
 * is not.
 */
export function shouldHideMagicLink() {
  return isNative();
}

/**
 * True when signup flows that depend on clicking an emailed confirmation link
 * must be hidden or reworked. Same reasoning as shouldHideMagicLink.
 *
 * Note for INT: this only matters if "Confirm email" is switched on for the
 * Supabase project. If it is off, native email signup works with no changes.
 * Verify the setting before Phase 1 rather than assuming either way.
 */
export function shouldHideEmailLinkSignup() {
  return isNative();
}

/**
 * Send the user to the real website to reset a forgotten password.
 *
 * The whole flow leaves the app, request included, and that is deliberate.
 * Supabase is in PKCE mode, so resetPasswordForEmail stores a code verifier in
 * the localStorage of whichever origin called it. Requesting the reset in the
 * app and completing it in Safari splits the verifier from the callback and the
 * exchange fails, which is the identical failure the canonical-host redirect in
 * AuthContext exists to prevent. Requesting and completing in the same system
 * browser is the only variant that works end to end. The user comes back and
 * signs in with the new password, which the native path handles natively.
 *
 * @returns {Promise<{ handled: boolean, error?: Error }>} handled false on web,
 * so the caller falls through to the existing in-app reset form untouched.
 */
export async function openPasswordResetInSystemBrowser(url = WEB_PASSWORD_RESET_URL) {
  if (!isNative()) return { handled: false };

  try {
    posthog.capture('native_password_reset_opened', { destination: 'system_browser' });
  } catch {}

  try {
    const mod = await loadNative(() => import('@capacitor/browser'));
    const Browser = mod?.Browser;
    if (Browser) {
      await Browser.open({ url, presentationStyle: 'popover' });
      return { handled: true };
    }
    // No plugin: still better to open somewhere than to do nothing silently.
    window.open(url, '_blank');
    return { handled: true };
  } catch (err) {
    try {
      posthog.capture('native_password_reset_failed', { message: String(err) });
    } catch {}
    return { handled: true, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
